/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react'
import {
  GetReadingLiveCallStatesSubscription,
  GetReadingLiveCallStatesSubscriptionVariables,
  GetReadingRoomBookParticipantsSubscription,
  GetReadingRoomBookParticipantsSubscriptionVariables,
  GetReadingRoomParticipantsSubscription,
  GetReadingRoomParticipantsSubscriptionVariables,
  useDeleteLiveCallStateMutation,
  useUpsertLiveCallStateMutation,
} from '../../../generated/graphql'
import GetReadingLiveCallStates from '../../../graphql/subscriptions/GetReadingLiveCallStates'
import useAuthClaims from '../../../hooks/useAuthClaims'
import useCustomSubscription from '../../../hooks/useCustomSubscription'
import { HLO } from '../../../types/global'
import resolveParticipationEventMeaning, {
  Options as ResolveParticipationEventOptions,
} from '../../../util/resolveParticipationEventMeaning'
import GetReadingRoomBookParticipants from '../graphql/subscriptions/GetReadingRoomBookParticipants'
import GetReadingRoomParticipants from '../graphql/subscriptions/GetReadingRoomParticipants'

export type LiveParticipants = {
  [uid: string]:
    | {
        name: string
        state: HLO.ParticipationState | HLO.ReadingState
        user: ReturnType<typeof useLiveUsers>['liveUsers'][0] | null
        isPresent: boolean
      }
    | undefined
}

export type Participants = {
  [uid: string]: {
    name: string
    isPresent: boolean
    state: HLO.ParticipationState | HLO.ReadingState
  }
}

export type ParticipantType = 'book' | 'reader'

export const readingStatePriority: Record<HLO.ReadingState, number> = {
  Scheduled: 1,
  Starting: 2,
  Canceled: 3,
  'In Progress': 4,
  Ending: 5,
  Incomplete: 6,
  Completed: 7,
}

export default function useLiveParticipants(readingId: string | undefined) {
  const {
    participants,
    loading: participantsLoading,
    error: participantsError,
  } = useParticipants(readingId)

  const { liveUsers } = useLiveUsers({
    readingId,
    skip: participantsLoading || Boolean(participantsError),
  })

  const liveParticipants: LiveParticipants = {}

  Object.keys(participants).forEach((uid) => {
    if (liveUsers[uid]) {
      liveParticipants[uid] = {
        ...participants[uid],
        user: liveUsers[uid],
      }
    }
  })

  return { liveParticipants }
}

export type UseParticipantsConfig = { readingId?: string }

export function useParticipants(readingId: string | undefined) {
  const { loading: authLoading } = useAuthClaims()

  const readerParticipants = useCustomSubscription<
    GetReadingRoomParticipantsSubscription,
    GetReadingRoomParticipantsSubscriptionVariables
  >({
    query: GetReadingRoomParticipants,
    variables: { reading_id: readingId },
    skip: authLoading || !readingId,
  })

  const bookParticipants = useCustomSubscription<
    GetReadingRoomBookParticipantsSubscription,
    GetReadingRoomBookParticipantsSubscriptionVariables
  >({
    query: GetReadingRoomBookParticipants,
    variables: { reading_id: readingId },
    skip: authLoading || !readingId,
  })

  const participants: Participants = {}

  const loading = readerParticipants.loading || bookParticipants.loading
  const error = readerParticipants.error || bookParticipants.error
  if (loading || error || !readerParticipants.data || !bookParticipants.data) {
    return { participants, loading, error }
  }

  const readerInfo: {
    [readerId: string]: {
      name: string
      events: { participationstate: string; created: string }[]
    }
  } = {}

  readerParticipants.data.view_reading_room_participants.forEach(
    ({ user_id, participationstate, created, person_name }) => {
      if (user_id && participationstate && created && person_name) {
        const updatedEntry: (typeof readerInfo)[0] = readerInfo[user_id]
          ? readerInfo[user_id]
          : { name: person_name, events: [] }

        updatedEntry.events.push({
          participationstate,
          created,
        })

        readerInfo[user_id] = updatedEntry
      }
    },
  )

  Object.keys(readerInfo).forEach((readerId) => {
    const reader = readerInfo[readerId]

    const events: ResolveParticipationEventOptions['events'] = []
    reader.events.forEach(({ participationstate, created }) => {
      events.push({
        participationstate: { name: participationstate },
        created,
      })
    })

    const { registered, isPresent } = resolveParticipationEventMeaning({
      events,
    })

    const latestEvent = [...reader.events].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
    )[0]

    if (registered && latestEvent) {
      participants[readerId] = {
        name: reader.name,
        isPresent: isPresent,
        state: latestEvent.participationstate as HLO.ParticipationState,
      }
    }
  })

  bookParticipants.data.view_reading_room_book.forEach(
    ({ user_id, person_name, reading }) => {
      if (user_id && person_name && reading) {
        var readingEvents =
          bookParticipants.data?.view_reading_room_book[0].reading
            ?.readingevents || ''

        if (Array.isArray(readingEvents) && readingEvents.length > 0) {
          const currentReadingEventStatusName = readingEvents
            .map((readingEvent) => readingEvent?.readingstate?.name)
            .filter((stateName): stateName is HLO.ReadingState =>
              Boolean(stateName),
            )
            .sort(
              (stateA, stateB) =>
                readingStatePriority[stateB] - readingStatePriority[stateA],
            )[0]

          if (!currentReadingEventStatusName) {
            return
          }

          participants[user_id] = {
            name: person_name,
            isPresent:
              currentReadingEventStatusName === 'In Progress' ||
              currentReadingEventStatusName === 'Ending',
            state: currentReadingEventStatusName as HLO.ReadingState,
          }
        }
      }
    },
  )

  return { participants, loading, error }
}

export type LiveUsers = {
  [uid: string]: GetReadingLiveCallStatesSubscription['live_call_state'][0]
}

export type useLiveUsersConfig = {
  readingId?: string
  skip?: boolean
}
export function useLiveUsers({ readingId, skip = false }: useLiveUsersConfig) {
  const { userId: userClaimId, loading: authLoading } = useAuthClaims()

  const [upsertLiveCallStateMutation, upsertLiveCallState] =
    useUpsertLiveCallStateMutation()

  const liveCallStates = useCustomSubscription<
    GetReadingLiveCallStatesSubscription,
    GetReadingLiveCallStatesSubscriptionVariables
  >({
    query: GetReadingLiveCallStates,
    variables: { reading_id: readingId },
    skip: skip || authLoading || !readingId,
  })

  useEffect(() => {
    if (!userClaimId || !readingId) return

    upsertLiveCallStateMutation({
      variables: {
        reading_id: readingId,
        user_id: userClaimId,
        is_hand_raised: false,
      },
    })
  }, [readingId, userClaimId])

  const [deleteLiveCallStateMutation, deleteLiveCallState] =
    useDeleteLiveCallStateMutation()

  useEffect(() => {
    if (userClaimId) {
      const deleteMyLiveCallState = () => {
        deleteLiveCallStateMutation({
          variables: { user_id: userClaimId },
        })
      }

      // const handleBeforePageLeave = (e: Event) => {
      //   e.preventDefault()
      //   // @ts-ignore
      //   return (e.returnValue = '')
      // }

      const handlePageLeave = (e: Event) => {
        deleteMyLiveCallState()
      }

      const removeListeners = () => {
        // window.removeEventListener('beforeunload', handleBeforePageLeave)
        window.removeEventListener('unload', handlePageLeave)
      }

      removeListeners()
      // window.addEventListener('beforeunload', handleBeforePageLeave)
      window.addEventListener('unload', handlePageLeave)

      return () => {
        deleteMyLiveCallState()
        removeListeners()
      }
    }
  }, [userClaimId])

  const liveUsers = useMemo<LiveUsers>(() => {
    const results: LiveUsers = {}
    if (!liveCallStates?.data) return results

    liveCallStates.data.live_call_state.forEach((user) => {
      results[user.user_id] = user
    })

    return results
  }, [liveCallStates.data?.live_call_state])

  return { liveUsers }
}
